import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Award, Loader2, ChevronLeft, Save, Heart, Gift, Settings, HelpCircle, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuthStore } from '@/store';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });

  const handleSave = async () => {
    setIsLoading(true);
    const result = await updateProfile(form);
    setIsLoading(false);
    
    if (result.success) {
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
            data-testid="back-button"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Profile</h1>
        </div>

        {/* Profile Info */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Personal Information</CardTitle>
              <Button
                variant={isEditing ? "ghost" : "outline"}
                size="sm"
                onClick={() => isEditing ? setIsEditing(false) : setIsEditing(true)}
                data-testid="edit-profile-btn"
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Full Name
              </Label>
              {isEditing ? (
                <Input
                  id="name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="profile-name"
                />
              ) : (
                <p className="text-foreground">{user?.full_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email
              </Label>
              <p className="text-foreground">{user?.email}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Phone
              </Label>
              {isEditing ? (
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Enter phone number"
                  data-testid="profile-phone"
                />
              ) : (
                <p className="text-foreground">{user?.phone || 'Not provided'}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Default Address
              </Label>
              {isEditing ? (
                <Textarea
                  id="address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Enter your default delivery address"
                  className="resize-none"
                  data-testid="profile-address"
                />
              ) : (
                <p className="text-foreground">{user?.address || 'Not provided'}</p>
              )}
            </div>

            {isEditing && (
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="w-full btn-pill"
                data-testid="save-profile-btn"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Loyalty Points */}
        <Card className="mb-6 cursor-pointer card-hover" onClick={() => navigate('/loyalty')}>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Loyalty Points</p>
                  <p className="text-2xl font-bold">{user?.loyalty_points || 0}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/favorites')}
            >
              <Heart className="h-5 w-5 mr-3 text-red-500" />
              <span className="flex-1 text-left">Favorites</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/addresses')}
            >
              <MapPin className="h-5 w-5 mr-3 text-blue-500" />
              <span className="flex-1 text-left">Address Book</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/reservations')}
            >
              <Calendar className="h-5 w-5 mr-3 text-green-500" />
              <span className="flex-1 text-left">My Reservations</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/referral')}
            >
              <Gift className="h-5 w-5 mr-3 text-purple-500" />
              <span className="flex-1 text-left">Invite Friends</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Separator className="my-2" />
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-5 w-5 mr-3 text-muted-foreground" />
              <span className="flex-1 text-left">Settings</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12"
              onClick={() => navigate('/help')}
            >
              <HelpCircle className="h-5 w-5 mr-3 text-muted-foreground" />
              <span className="flex-1 text-left">Help & Support</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardContent className="py-4">
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="w-full"
              data-testid="logout-btn"
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
